import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PipelineProjectService, {
  type OffBaselineDisposition,
  type OffBaselineLine,
} from '@frontend/AppSettings/services/PipelineProjectService';
import { useBidCapabilities } from '@frontend/BidSheets/useBidCapabilities';
import { money, useFinancialVisible } from '@frontend/BidSheets/financialDisplay';

/**
 * Classification and its consequences (domain-model-transition P7b, migration 0050).
 *
 * THREE SURFACES ON ONE TAB, and they are separate on purpose:
 *
 *  * THE OFF-BASELINE QUEUE. A supervisor ANNOTATES (they are the only person who knows why the
 *    pair was off plan) and a PM DISPOSES under `MUTATE_BIDS`. The annotation SURVIVES the
 *    disposition — a record of who decided, without the why, is not accountability.
 *  * THE FORCE-ACCOUNT RELEASE QUEUE. One act, two gates: payroll approval is the statutory
 *    judgement that the hours are real and correctly classified; the release is the commercial
 *    judgement that they are chargeable. It GATES and never rewrites the frozen billability — a
 *    line the PM will not charge is REJECTED, which withholds it from billing while leaving the
 *    derivation intact.
 *  * THE RECLASSIFICATION REPORT. Its second dimension is EMPTY, and rendering it blank would read
 *    as the field and the statute AGREEING — which is the failure it exists to prevent. So the
 *    reason is rendered where the table would have been. That is a requirement, not a nicety.
 *
 * THE PERIOD-CLOSE DELTA IS DELIBERATELY NOT HERE. It gets its own surface, because this report's
 * headline is a disagreement RATE and a rate cannot be computed when its denominator is three
 * different things (decisions D6).
 */

/** The wire's string figures, safely: '' / null / undefined are "no figure", never 0. `money`
 * hides null itself, but `Number(null)` is 0 — so `money(Number(x))` renders a redacted or
 * absent total as `$0`, which defeats the null redaction (the `money(num(...))` precedent). */
const num = (v: string | null | undefined): number | undefined => {
  if (v === null || v === undefined || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

const DISPOSITIONS: { value: OffBaselineDisposition; label: string }[] = [
  { value: 'absorbed', label: 'Absorbed' },
  { value: 'force_account', label: 'Force account' },
  { value: 'change_order', label: 'Change order' },
];

interface ClassificationTabProps { matterid: string }

const ClassificationTab = ({ matterid }: ClassificationTabProps): JSX.Element => {
  const queryClient = useQueryClient();
  const financialVisible = useFinancialVisible();
  const capabilities = useBidCapabilities();
  // Wire values, not the constants' names: roles.py sends lowercase (the CommercialTab rule).
  const mayDispose = capabilities?.capabilities.includes('mutate_bids') ?? false;
  const mayAnnotate = capabilities?.capabilities.includes('record_production') ?? false;
  const [showDisposed, setShowDisposed] = useState(false);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const queue = useQuery({
    queryKey: ['OffBaseline', matterid, showDisposed],
    queryFn: () => PipelineProjectService.getOffBaseline(matterid, showDisposed),
  });
  const held = useQuery({
    queryKey: ['HeldForceAccount', matterid],
    queryFn: () => PipelineProjectService.getHeldForceAccount(matterid),
  });
  const report = useQuery({
    queryKey: ['ReclassificationReport', matterid],
    queryFn: () => PipelineProjectService.getReclassificationReport(matterid),
  });

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['OffBaseline', matterid] }),
      queryClient.invalidateQueries({ queryKey: ['HeldForceAccount', matterid] }),
      queryClient.invalidateQueries({ queryKey: ['ReclassificationReport', matterid] }),
    ]);
  };

  const disposeMutation = useMutation({
    mutationFn: ({ lineId, disposition }: { lineId: string;
      disposition: OffBaselineDisposition }) =>
      PipelineProjectService.disposeOffBaseline(matterid, lineId, disposition),
    onSuccess: () => { setError(null); return invalidate(); },
    onError: (e: Error) => setError(e.message),
  });

  const annotateMutation = useMutation({
    mutationFn: ({ lineId, note }: { lineId: string; note: string }) =>
      PipelineProjectService.annotateOffBaseline(matterid, lineId, note || null),
    onSuccess: () => { setError(null); return invalidate(); },
    onError: (e: Error) => setError(e.message),
  });

  const releaseMutation = useMutation({
    mutationFn: ({ lineIds, status }: { lineIds: string[];
      status: 'released' | 'rejected' }) =>
      PipelineProjectService.setForceAccountRelease(matterid, lineIds, status),
    onSuccess: () => { setError(null); return invalidate(); },
    onError: (e: Error) => setError(e.message),
  });

  const noteFor = (line: OffBaselineLine) => notes[line.id] ?? line.note ?? '';
  const heldLines = useMemo(() => held.data ?? [], [held.data]);

  return (
    <div className="pipeline-tab-body">
      {error && <div className="pipeline-error" role="alert">{error}</div>}

      <section className="pipeline-card">
        <h3>Off-baseline entries</h3>
        <p className="pipeline-muted">
          Work recorded against a phase and cost code that was not on the baseline version in
          force when it was entered. The judgement is made once, at entry: a baseline approved
          afterwards neither flags nor unflags anything already recorded. The queue starts empty
          and fills forward — nothing before this release was ever compared to a baseline.
        </p>
        <label className="pipeline-check-label">
          <input
            type="checkbox" checked={showDisposed}
            onChange={(e) => setShowDisposed(e.target.checked)}
          />
          Show entries already dispositioned
        </label>
        {queue.isLoading && <p>Loading…</p>}
        {/* A FAILED READ IS NOT AN EMPTY QUEUE. Rendering "nothing off baseline" on an error makes
            a positive claim about the project out of a failure to ask — the same misreading the
            empty report dimension exists to prevent, one surface over. */}
        {queue.isError && (
          <p className="pipeline-error" role="alert">
            Could not load the off-baseline queue. This is NOT a statement that the project has
            none — try again.
          </p>
        )}
        {!queue.isLoading && !queue.isError && (queue.data ?? []).length === 0 && (
          <p className="pipeline-muted">Nothing off baseline on this project.</p>
        )}
        {(queue.data ?? []).map((line) => (
          <div className="pipeline-line-row" key={line.id}>
            <span>{line.ticket_date}</span>
            <span>{line.employee_name ?? line.description}</span>
            <span>{line.phase ?? '(no phase)'} · {line.cost_code ?? '(no cost code)'}</span>
            <span>{line.hours} h</span>
            <label>
              Why
              <input
                value={noteFor(line)}
                readOnly={!mayAnnotate}
                onChange={(e) => setNotes({ ...notes, [line.id]: e.target.value })}
                // Only when it CHANGED. Blur fires on every tab-out, and a PUT per blur writes an
                // audit row saying nothing moved.
                onBlur={() => {
                  if (!mayAnnotate) return;
                  // In flight = hands off (R15): the change detection below compares against the
                  // last FETCHED note, so a second blur inside the in-flight window still reads
                  // as "changed" and sends a duplicate PUT with a duplicate audit row.
                  if (annotateMutation.isPending) return;
                  if (noteFor(line) === (line.note ?? '')) return;
                  annotateMutation.mutate({ lineId: line.id, note: noteFor(line) });
                }}
              />
            </label>
            {line.disposition ? (
              // The annotation is rendered BESIDE the decision rather than replaced by it.
              <span className="pipeline-muted">
                {line.disposition} · {line.disposition_at?.slice(0, 10)}
              </span>
            ) : (
              <label>
                Disposition
                <select
                  value=""
                  // `isPending` too (R14): a disposition is a one-way judgement with a
                  // server-stamped actor and time — a double-change during the in-flight window
                  // must not race two decisions.
                  disabled={!mayDispose || disposeMutation.isPending}
                  onChange={(e) => disposeMutation.mutate({
                    lineId: line.id,
                    disposition: e.target.value as OffBaselineDisposition })}
                >
                  <option value="">Decide…</option>
                  {DISPOSITIONS.map((d) => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </label>
            )}
          </div>
        ))}
      </section>

      <section className="pipeline-card">
        <h3>Force-account hours awaiting release</h3>
        <p className="pipeline-muted">
          Hours the commercial sweep carried onto a force-account ticket when the person-day was
          approved. Approval said the hours are real and correctly classified; releasing says they
          are chargeable. Rejecting withholds a line from billing and changes nothing about how it
          was priced.
        </p>
        {held.isLoading && <p>Loading…</p>}
        {held.isError && (
          <p className="pipeline-error" role="alert">
            Could not load the release queue. Hours may be waiting — try again.
          </p>
        )}
        {!held.isLoading && !held.isError && heldLines.length === 0 && (
          <p className="pipeline-muted">No swept hours are waiting.</p>
        )}
        {heldLines.map((line) => (
          <div className="pipeline-line-row" key={line.id}>
            <span>{line.description}</span>
            <span>{line.hours} h</span>
            {/* The frozen money strips for a blind reader, so it is rendered only when visible —
                never as a zero, which would read as free work. `num`, not `Number`: a stripped
                or absent total must render REDACTED/absent, and `Number(null)` is 0 (R15). */}
            {financialVisible && <span>{money(num(line.line_total))}</span>}
            {/* `isPending` as well as capability (R14) — the guard CommercialTab.toggleLine
                already keeps. A double-click double-fired the release, re-stamping released_by
                and minting a second audit row; a Release-then-Reject race inside the refetch
                window flipped a released line with no on-screen record. */}
            <button
              type="button" className="pipeline-secondary-btn"
              disabled={!mayDispose || releaseMutation.isPending}
              onClick={() => releaseMutation.mutate(
                { lineIds: [line.id as string], status: 'released' })}
            >
              Release
            </button>
            <button
              type="button" className="pipeline-secondary-btn"
              disabled={!mayDispose || releaseMutation.isPending}
              onClick={() => releaseMutation.mutate(
                { lineIds: [line.id as string], status: 'rejected' })}
            >
              Reject
            </button>
          </div>
        ))}
      </section>

      <section className="pipeline-card">
        <h3>Reclassification report</h3>
        {report.isLoading && <p>Loading…</p>}
        {report.isError && (
          <p className="pipeline-error" role="alert">
            Could not load the reclassification report. A blank report reads as the field and the
            statute agreeing, which is exactly what it must never say — try again.
          </p>
        )}
        {report.data && (
          <>
            <h4>Billing basis — field against verified</h4>
            {report.data.basis_disagreement.available ? (
              <>
                <p>
                  {report.data.basis_disagreement.disagreed} of{' '}
                  {report.data.basis_disagreement.considered} approved lines had their basis
                  changed by the office — a rate of
                  {' '}{report.data.basis_disagreement.disagreement_rate}.
                </p>
                <ul>
                  {report.data.basis_disagreement.by_supervisor.map((row) => (
                    <li key={`${row.supervisor}-${row.matterid}`}>
                      {row.supervisor ?? '(unattributed)'}: {row.disagreed}/{row.considered}
                      {' '}({row.disagreement_rate})
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              // NOT an empty table. A dimension that renders nothing reads as agreement.
              <p className="pipeline-muted">
                {report.data.basis_disagreement.reason
                  ?? 'This dimension is empty and the server did not say why — which is itself '
                     + 'worth reporting, because a blank one reads as agreement.'}
              </p>
            )}

            <h4>Entered hours against the statute</h4>
            {report.data.entered_vs_statutory.available ? (
              <p>
                {report.data.entered_vs_statutory.with_residual} lines claimed straight time with
                a residual; the statute found a premium on{' '}
                {report.data.entered_vs_statutory.statutory_premium_lines}.
              </p>
            ) : (
              // THE REQUIREMENT, not a placeholder: this dimension is empty, and saying so is
              // the whole point — an empty table would read as the field and the statute
              // agreeing, which is the failure this report exists to prevent.
              <p className="pipeline-muted" data-testid="entered-vs-statutory-empty">
                {report.data.entered_vs_statutory.reason
                  ?? 'This dimension is empty and the server did not say why — which is itself '
                     + 'worth reporting, because a blank one reads as the field and the statute '
                     + 'agreeing.'}
              </p>
            )}

            <h4>Which rules were applied</h4>
            <p className="pipeline-muted">
              {report.data.ruleset_provenance.rules_applied === 0
                ? 'No hours on this project have been classified yet.'
                : `${report.data.ruleset_provenance.rules_applied} rule version(s) governed these `
                  + `hours${report.data.ruleset_provenance.changed_within_window
                    ? ' — the rules CHANGED inside this window, which is why a re-run can move '
                      + 'numbers.' : '.'}`}
              {report.data.ruleset_provenance.override_dates_withheld
                && ' A recorded position of this employer applied; its effective date is not '
                  + 'shown to callers who cannot see commercial detail.'}
            </p>
            <ul>
              {report.data.ruleset_provenance.entries.map((entry, i) => (
                <li key={`${entry.jurisdiction}-${entry.source}-${i}`}>
                  {entry.jurisdiction} · {entry.source}
                  {entry.effective_from ? ` from ${entry.effective_from}` : ' (date withheld)'}
                  {' '}· {entry.hours} h
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
    </div>
  );
};

export default ClassificationTab;
